import { useCallback, useEffect, useRef, useState } from 'react';
import { useMap, InfoWindow } from '@vis.gl/react-google-maps';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useListContext, useTranslate } from 'react-admin';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import LocationIcon from '@/assets/icons/location-icon.svg';
import LocationErrorIcon from '@/assets/icons/location-error-icon.svg';
import CheckboxCheckedIcon from '@/assets/icons/checkbox-checked-icon.svg';
import { Palette } from '@/themes/themeOverrides/Palette';
import ServiceDate from '@/components/service/ServiceDate';

const StyledCollectionPointInfo = styled(Box)({
    width: '100%',
});

const StyledCollectionPointId = styled(Typography)({
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: '600',
    display: 'block',
    marginBottom: '12px',
});

const StyledPosText = styled(Typography)({
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: '400',
    display: 'block',
    marginBottom: '0px',
});

const StyledCollectionPointDates = styled(Box)({
    borderTop: `2px solid ${Palette.gray.gray200}`,
    paddingTop: '10px',
    marginTop: '6px',
    '& a': {
        display: 'block',
        fontWeight: '600 !important',
        marginBottom: '2px',
        fontSize: '11px',
    },
    '& .date-field': {
        top: 'auto',
        padding: '2px 0',
        '& + .date-field': {
            marginTop: '0px',
        },
        '& > .MuiBox-root': {
            padding: '2px 0 2px',
        },
        '& .device-status-field-time': {
            top: 'auto',
            padding: '5px 0 0px',
        },
        '& .map-date-field-label': {
            fontSize: '11px',
            fontWeight: '400',
            lineHeight: '16px',
            color: Palette.gray.gray900,
        },
    },
    '& .no-term': {
        fontSize: '11px',
        fontWeight: '400',
        lineHeight: '16px',
        color: Palette.gray.gray500,
    },
});

const getValidPositions = collectionPointData =>
    collectionPointData
        .map(collectionPoint => {
            const latitude = Number(collectionPoint?.geoPoint?.latitude ?? collectionPoint?.geoPoint?.lat ?? collectionPoint?.latitude);
            const longitude = Number(collectionPoint?.geoPoint?.longitude ?? collectionPoint?.geoPoint?.lng ?? collectionPoint?.longitude);
            return { ...collectionPoint, latitude, longitude };
        })
        .filter(collectionPoint => Number.isFinite(collectionPoint?.latitude) && Number.isFinite(collectionPoint?.longitude))
        .slice()
        .sort((a, b) => String(a?.id).localeCompare(String(b?.id)));

const buildSignature = positions =>
    JSON.stringify(
        positions.map(collectionPoint => [
            String(collectionPoint?.id),
            collectionPoint.latitude,
            collectionPoint.longitude,
            String(collectionPoint?.customId || ''),
            String(collectionPoint?.inspectionId || ''),
        ]),
    );

const clearMarkers = markersRef => {
    markersRef.current.forEach(item => {
        if (item?.type === 'advanced') {
            item.marker.map = null;
            return;
        }
        if (item?.marker?.setMap) {
            item.marker.setMap(null);
        }
    });
    markersRef.current = [];
};

const getIconSrc = ({ hasOfflineDevice, isSelected }) => {
    if (isSelected) {
        return CheckboxCheckedIcon;
    }
    return hasOfflineDevice ? LocationErrorIcon : LocationIcon;
};

const getIconHtml = ({ hasOfflineDevice, isSelected }) =>
    `<img class="pos-marker-icon" src="${getIconSrc({ hasOfflineDevice, isSelected })}" width="16" height="16" alt="" />`;

const getCheckboxHtml = ({ isSelected, hasInspection, collectionPoint }) => {
    return `
        <input
            type="checkbox"
            id="pos-${collectionPoint.id}"
            class="pos-marker-checkbox"
            ${isSelected ? 'checked' : ''}
            ${hasInspection ? '' : 'disabled'}
            style="width: 16px; height: 16px; cursor: pointer; position: absolute; top: 0; left: 0; z-index: 2; opacity: 0;"
        />
        <label for="pos-${collectionPoint.id}" class="pos-marker-label" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer; z-index: 3; opacity: 0;">${collectionPoint.id}</label>
    `;
};

const createMarkerElement = ({ iconHtml, isSelected, hasInspection, collectionPoint }) => {
    const markerElement = document.createElement('div');
    markerElement.style.position = 'relative';
    const checkboxHtml = getCheckboxHtml({ isSelected, hasInspection, collectionPoint });
    markerElement.innerHTML = `
        <div style="position: relative; display: flex; align-items: center; justify-content: center;" class="pos-marker-container">
            <div style="position: relative; overflow: hidden;">
                ${checkboxHtml}
                <div style="
                    width: 32px;
                    height: 32px;
                    background-color: #FFF;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    position: relative;
                ">
                    ${iconHtml}
                </div>
            </div>
        </div>
    `;
    return markerElement;
};

const createMarkerInstance = ({ map, markerElement, markerPosition, markerTitle, hasOfflineDevice, useAdvancedMarkers }) => {
    if (useAdvancedMarkers) {
        return new google.maps.marker.AdvancedMarkerElement({
            map,
            position: markerPosition,
            title: markerTitle,
            content: markerElement,
        });
    }
    return new google.maps.Marker({
        map,
        position: markerPosition,
        title: markerTitle,
        icon: {
            url: hasOfflineDevice ? LocationErrorIcon : LocationIcon,
            scaledSize: new google.maps.Size(24, 24),
        },
    });
};

const getSelectionHandler =
    ({ collectionPoint, onSelect, selectedIdsRef }) =>
    event => {
        event.stopPropagation();
        const currentSelected = selectedIdsRef.current || [];
        const nextSelected = event.target.checked
            ? [...new Set([...currentSelected, collectionPoint.inspectionId])]
            : currentSelected.filter(id => id !== collectionPoint.inspectionId);
        onSelect(nextSelected);
    };

const attachMarkerEvents = ({
    useAdvancedMarkers,
    markerElement,
    marker,
    collectionPoint,
    onSelect,
    selectedIdsRef,
    setSelectedCollectionPoint,
    setSelectedMarker,
    scheduleHide,
    clearHide,
}) => {
    const showInfo = () => {
        clearHide();
        setSelectedCollectionPoint(collectionPoint);
        setSelectedMarker(marker);
    };
    const hideInfo = () => {
        scheduleHide();
    };
    const checkbox = markerElement.querySelector('.pos-marker-checkbox');

    if (useAdvancedMarkers) {
        if (checkbox && collectionPoint?.inspectionId) {
            checkbox.addEventListener('click', event => event.stopPropagation());
            checkbox.addEventListener('change', getSelectionHandler({ collectionPoint, onSelect, selectedIdsRef }));
        }
        markerElement.addEventListener('mouseenter', showInfo);
        markerElement.addEventListener('mouseleave', hideInfo);
        marker.addListener('gmp-click', showInfo);
        return checkbox;
    }

    marker.addListener('mouseover', showInfo);
    marker.addListener('mouseout', hideInfo);
    marker.addListener('click', showInfo);
    return checkbox;
};

const createMarkerEntry = ({
    map,
    collectionPoint,
    useAdvancedMarkers,
    selectedIdsRef,
    onSelect,
    setSelectedCollectionPoint,
    setSelectedMarker,
    scheduleHide,
    clearHide,
}) => {
    const hasOfflineDevice = collectionPoint
        ? collectionPoint.deviceState === 'offline' ||
          collectionPoint.deviceState === 'codeError' ||
          collectionPoint.deviceState === 'binFull' ||
          collectionPoint.deviceState === 'permanentError' ||
          collectionPoint.deviceState === 'outOfService' ||
          collectionPoint.deviceState === null
        : false;
    const isSelected = collectionPoint?.inspectionId ? selectedIdsRef.current?.includes(collectionPoint.inspectionId) : false;
    const iconHtml = getIconHtml({ hasOfflineDevice, isSelected });
    const markerElement = createMarkerElement({ iconHtml, isSelected, hasInspection: Boolean(collectionPoint?.inspectionId), collectionPoint });
    const markerTitle = `${collectionPoint?.customId ?? ''} ${collectionPoint?.shopName ?? ''}`.trim();
    const markerPosition = { lat: collectionPoint.latitude, lng: collectionPoint.longitude };
    const marker = createMarkerInstance({ map, markerElement, markerPosition, markerTitle, hasOfflineDevice, useAdvancedMarkers });
    const checkbox = attachMarkerEvents({
        useAdvancedMarkers,
        markerElement,
        marker,
        collectionPoint,
        onSelect,
        selectedIdsRef,
        setSelectedCollectionPoint,
        setSelectedMarker,
        scheduleHide,
        clearHide,
    });

    return {
        marker,
        type: useAdvancedMarkers ? 'advanced' : 'default',
        checkbox,
        markerElement,
        hasOfflineDevice,
        inspectionId: collectionPoint?.inspectionId,
    };
};

export const ServiceInspectionCollectionPointMarkers = ({ collectionPointData }) => {
    const translate = useTranslate();
    const { selectedIds, onSelect } = useListContext();
    const selectedIdsRef = useRef(selectedIds);
    const map = useMap();
    const lastSignatureRef = useRef(null);
    const markersRef = useRef([]);
    const isTooltipHoverRef = useRef(false);
    const hideTimeoutRef = useRef(null);
    const [selectedCollectionPoint, setSelectedCollectionPoint] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const clearHide = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const scheduleHide = useCallback(() => {
        clearHide();
        hideTimeoutRef.current = setTimeout(() => {
            if (isTooltipHoverRef.current) {
                return;
            }
            setSelectedCollectionPoint(null);
            setSelectedMarker(null);
        }, 120);
    }, [clearHide]);

    useEffect(() => {
        if (!map || !collectionPointData) return;

        const validPositions = getValidPositions(collectionPointData);
        const signature = buildSignature(validPositions);

        if (lastSignatureRef.current === signature) {
            return;
        }

        clearMarkers(markersRef);

        const useAdvancedMarkers = Boolean(google?.maps?.marker?.AdvancedMarkerElement);

        markersRef.current = validPositions.map(collectionPoint =>
            createMarkerEntry({
                map,
                collectionPoint,
                useAdvancedMarkers,
                selectedIdsRef,
                onSelect,
                setSelectedCollectionPoint,
                setSelectedMarker,
                scheduleHide,
                clearHide,
            }),
        );

        lastSignatureRef.current = signature;

        return () => {
            markersRef.current.forEach(item => {
                if (item?.type === 'advanced') {
                    item.marker.map = null;
                    return;
                }
                if (item?.marker?.setMap) {
                    item.marker.setMap(null);
                }
            });
            markersRef.current = [];
        };
    }, [map, collectionPointData, onSelect, scheduleHide, clearHide]);

    useEffect(() => () => clearHide(), [clearHide]);

    useEffect(() => {
        selectedIdsRef.current = selectedIds;
        markersRef.current.forEach(item => {
            if (!item?.checkbox || !item?.inspectionId) return;
            item.checkbox.checked = selectedIds.includes(item.inspectionId);
            const isSelected = selectedIds.includes(item.inspectionId);
            const iconSrc = getIconSrc({ hasOfflineDevice: item.hasOfflineDevice, isSelected });
            if (item.type === 'advanced') {
                const iconElement = item.markerElement?.querySelector('.pos-marker-icon');
                if (iconElement && iconElement.getAttribute('src') !== iconSrc) {
                    iconElement.setAttribute('src', iconSrc);
                }
                return;
            }
            if (item.marker?.setIcon) {
                item.marker.setIcon({
                    url: iconSrc,
                    scaledSize: new google.maps.Size(24, 24),
                });
            }
        });
    }, [selectedIds]);

    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('click', () => {
            setSelectedCollectionPoint(null);
            setSelectedMarker(null);
        });
        return () => listener?.remove();
    }, [map]);

    const addressLine = selectedCollectionPoint?.address?.address ?? selectedCollectionPoint?.address ?? '';
    const postalCode = selectedCollectionPoint?.address?.postalCode ?? selectedCollectionPoint?.postalCode ?? '';
    const city = selectedCollectionPoint?.address?.city ?? selectedCollectionPoint?.city ?? '';
    const dueDate = selectedCollectionPoint?.dueDate;
    const scheduledDate = selectedCollectionPoint?.scheduledDate;
    const serialNumber = selectedCollectionPoint?.serialNumber;
    const deviceId = selectedCollectionPoint?.deviceId;

    return selectedCollectionPoint && selectedMarker ? (
        <InfoWindow
            anchor={selectedMarker}
            onCloseClick={() => {
                setSelectedCollectionPoint(null);
                setSelectedMarker(null);
            }}
        >
            <Box
                sx={{ '& a:focus-visible': { outline: 'none', boxShadow: 'none' } }}
                onMouseEnter={() => {
                    isTooltipHoverRef.current = true;
                    clearHide();
                }}
                onMouseLeave={() => {
                    isTooltipHoverRef.current = false;
                    scheduleHide();
                }}
            >
                <DrawerLink
                    resource='collection-point'
                    source={selectedCollectionPoint.id}
                    action='show'
                    relative
                    label={
                        <StyledCollectionPointInfo>
                            <StyledCollectionPointId variant='caption'>{selectedCollectionPoint.customId}</StyledCollectionPointId>
                            {addressLine ? <StyledPosText variant='caption'>{addressLine}</StyledPosText> : null}
                            {(postalCode || city) && <StyledPosText variant='caption'>{`${postalCode} ${city}`.trim()}</StyledPosText>}
                            {!addressLine && !postalCode && !city ? (
                                <StyledPosText variant='caption' sx={{ color: Palette.gray.gray500 }}>
                                    -
                                </StyledPosText>
                            ) : null}
                        </StyledCollectionPointInfo>
                    }
                    sx={{ width: '100%' }}
                />
                <StyledCollectionPointDates>
                    {deviceId ? <DrawerLink resource='device' source={deviceId} action='show' label={serialNumber} relative /> : null}
                    {dueDate ? (
                        <Box className='date-field'>
                            <ServiceDate formatSince={dueDate} map mapLabel={`${translate('resources.serviceInspection.labels.mapDueDateLabel')}: `} />
                        </Box>
                    ) : (
                        <span className='no-term'>{translate('resources.serviceInspection.labels.noTerm')}</span>
                    )}
                    {scheduledDate ? (
                        <Box className='date-field'>
                            <ServiceDate formatSince={scheduledDate} map mapLabel={`${translate('resources.serviceInspection.labels.plannedDate')}: `} />
                        </Box>
                    ) : (
                        <span className='no-term'>{translate('resources.serviceInspection.labels.noTerm')}</span>
                    )}
                </StyledCollectionPointDates>
            </Box>
        </InfoWindow>
    ) : null;
};

ServiceInspectionCollectionPointMarkers.propTypes = {
    collectionPointData: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            customId: PropTypes.string,
            shopName: PropTypes.string,
            address: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    address: PropTypes.string,
                    postalCode: PropTypes.string,
                    city: PropTypes.string,
                }),
            ]),
            geoPoint: PropTypes.shape({
                latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            }),
            inspectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            dueDate: PropTypes.string,
            scheduledDate: PropTypes.string,
            postalCode: PropTypes.string,
            city: PropTypes.string,
            devices: PropTypes.arrayOf(
                PropTypes.shape({
                    state: PropTypes.string,
                }),
            ),
        }),
    ).isRequired,
};
